import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import {
  SvgIcon,
  SvgNames,
  TextField,
  tokens,
  Button,
  LoadingIndicator,
} from '@sunrun/experience-ui-components';

interface AutocompleteOption {
  value: string;
  label: string;
}

interface AutocompleteProps {
  options: AutocompleteOption[];
  value: string;
  onChange: (value: string) => void;
  onSelect?: (option: AutocompleteOption) => void;
  onClear?: () => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  testId?: string;
  isLoading?: boolean;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  options,
  value,
  onChange,
  onSelect,
  onClear,
  placeholder,
  label,
  disabled = false,
  isLoading = false,
  className,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase()),
  );

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (option: AutocompleteOption) => {
    setInputValue(option.label);
    onChange(option.label);
    onSelect?.(option);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1,
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = (event: React.FocusEvent) => {
    // Delay closing to allow option clicks
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }, 150);
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <AutocompleteContainer ref={containerRef} className={className}>
      <TextFieldContainer>
        <CustomTextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          label={label}
          disabled={disabled || isLoading}
          data-testid={testId}
          autoComplete="off"
        />
        {isLoading && (
          <CustomLoading width={20} height={20} showLoadingMessage={false} />
        )}
        {inputValue && !isLoading && (
          <CloseButton
            color="tertiary"
            size="xs"
            style={{ width: '20px', height: '20px' }}
            iconMiddle={
              <SvgIcon name={SvgNames.Close} color={tokens.BRAND_HERO_BLUE} />
            }
            onClick={() => {
              setInputValue('');
              onChange('');
              onClear?.();
              setHighlightedIndex(-1);
            }}
          />
        )}
      </TextFieldContainer>

      {isOpen && filteredOptions.length > 0 && (
        <OptionsList ref={listRef} data-testid={`${testId}-options`}>
          {filteredOptions.map((option, index) => (
            <OptionItem
              key={option.value}
              highlighted={index === highlightedIndex}
              onClick={() => handleOptionSelect(option)}
              data-testid={`${testId}-option-${index}`}>
              {option.label}
            </OptionItem>
          ))}
        </OptionsList>
      )}

      {isOpen && filteredOptions.length === 0 && inputValue && (
        <OptionsList data-testid={`${testId}-no-options`}>
          <NoOptionsMessage>No options found</NoOptionsMessage>
        </OptionsList>
      )}
    </AutocompleteContainer>
  );
};

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const OptionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  margin-top: 21px;
  padding: 0;
  list-style: none;
`;

const OptionItem = styled.li<{ highlighted: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  background: ${(props) =>
    props.highlighted ? tokens.TINTS_OFF_WHITE_40 : 'white'};
  color: ${tokens.BLACK};
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${tokens.TINTS_OFF_WHITE_40};
  }

  &:focus {
    outline: 2px solid ${tokens.BRAND_HERO_BLUE};
    outline-offset: -2px;
  }
`;

const NoOptionsMessage = styled.div`
  padding: 12px 16px;
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-style: italic;
  text-align: center;
`;

const TextFieldContainer = styled.div`
  width: 100%;
  position: relative;
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 16px;
  bottom: 15px;
  z-index: 1;
`;

const CustomLoading = styled(LoadingIndicator)`
  position: absolute;
  right: 16px;
  bottom: 15px;
  z-index: 1;
`;

const CustomTextField = styled(TextField)`
  input {
    max-width: 100%;
    padding-right: 36px;
  }
`;

export { Autocomplete };
export type { AutocompleteOption, AutocompleteProps };
